import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  AnalyticsResponse,
  SessionsResponse,
  PromptsResponse,
  SystemStatus,
  ApiError,
  Prompt,
  PromptCreate,
  PromptUpdate,
  PromptStats
} from '../types/api';

// Type for the token provider function
type TokenProvider = () => Promise<string | null>;

class ApiClient {
  private client: AxiosInstance;
  private tokenProvider: TokenProvider | null = null;

  constructor(baseURL?: string) {
    // Use environment variable or fallback to localhost
    const apiUrl = baseURL || 
                   process.env.REACT_APP_API_URL || 
                   'http://localhost:8000';

    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config) => {
        if (this.tokenProvider) {
          const token = await this.tokenProvider();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear any cached tokens and redirect to login
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Set the token provider function (will be called by AuthContext)
  setTokenProvider(provider: TokenProvider) {
    this.tokenProvider = provider;
  }

  // Set the auth token
  setAuthToken(token: string) {
    this.tokenProvider = async () => token;
  }

  // Clear the auth token
  clearAuth() {
    this.tokenProvider = null;
  }

  // Check if there is a token provider
  isAuthenticated(): boolean {
    return !!this.tokenProvider;
  }

  // Analytics endpoints
  async getAnalytics(): Promise<AnalyticsResponse> {
    const response: AxiosResponse<AnalyticsResponse> = await this.client.get('/admin/analytics');
    return response.data;
  }

  async resetAnalytics(): Promise<{ message: string; analytics: any }> {
    const response = await this.client.post('/admin/analytics/reset');
    return response.data;
  }

  // Session management endpoints
  async getSessions(): Promise<SessionsResponse> {
    const response: AxiosResponse<SessionsResponse> = await this.client.get('/admin/sessions');
    return response.data;
  }

  async clearSession(sessionId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/sessions/${sessionId}`);
    return response.data;
  }

  async clearAllSessions(): Promise<{ message: string }> {
    const response = await this.client.delete('/admin/sessions');
    return response.data;
  }

  // Prompt CRUD endpoints
  async createPrompt(promptData: PromptCreate): Promise<Prompt> {
    const response: AxiosResponse<Prompt> = await this.client.post('/admin/prompts', promptData);
    return response.data;
  }

  async getPrompts(
    page: number = 1, 
    pageSize: number = 100, 
    category?: string,
    team?: string,
    search?: string
  ): Promise<PromptsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    if (category) params.append('category', category);
    if (team) params.append('team', team);
    if (search) params.append('search', search);
    
    const response: AxiosResponse<PromptsResponse> = await this.client.get(
      `/admin/prompts?${params.toString()}`
    );
    return response.data;
  }

  async getPromptById(promptId: string): Promise<Prompt> {
    const response: AxiosResponse<Prompt> = await this.client.get(`/admin/prompts/${promptId}`);
    return response.data;
  }

  async updatePrompt(promptId: string, promptData: PromptUpdate): Promise<Prompt> {
    const response: AxiosResponse<Prompt> = await this.client.put(
      `/admin/prompts/${promptId}`, 
      promptData
    );
    return response.data;
  }

  async deletePrompt(promptId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/prompts/${promptId}`);
    return response.data;
  }

  async getPromptStats(): Promise<{ status: string; statistics: PromptStats }> {
    const response = await this.client.get('/admin/prompts/stats/overview');
    return response.data;
  }

  // System status endpoints
  async getSystemStatus(): Promise<SystemStatus> {
    const response: AxiosResponse<SystemStatus> = await this.client.get('/admin/system/status');
    return response.data;
  }

  // Health check (public endpoint)
  async getHealth(): Promise<any> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // Test Notion connection (public endpoint)
  async testNotionConnection(): Promise<any> {
    const response = await this.client.get('/notion/test');
    return response.data;
  }

  // Refresh Notion cache (public endpoint)
  async refreshNotionCache(): Promise<any> {
    const response = await this.client.post('/notion/refresh');
    return response.data;
  }

  // Embedding management endpoints
  async generateNewEmbeddings(): Promise<{ 
    success: boolean; 
    message: string; 
    new_prompts_count: number; 
    total_embeddings: number; 
    new_prompt_ids?: string[] 
  }> {
    const response = await this.client.post('/admin/embeddings/generate');
    return response.data;
  }
}

// Create a singleton instance
export const apiClient = new ApiClient();
export default ApiClient; 